import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard, DateRangeSelector, TrendIndicator } from '../components/StatCard';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { NavBar } from '../components/SubscriptionAppNavBar';
import { DateRangeTabs, DateRangeOption } from '../components/DateRangeTabs';
import { Users, DollarSign, TrendingDown, ShoppingCart, AlertTriangle, Calendar, MessageSquare, RefreshCw, CreditCard, Mail, Search, Sparkles, ArrowRight, CheckCircle, Clock, TrendingUp, Info, Smartphone, ClipboardList, Plus, Link as LinkIcon } from 'lucide-react';
import { useActivities, customerNames } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
import { CreateTaskModal } from '../components/CreateTaskModal';
import { SubscriptionActivityCard } from '../components/SubscriptionActivityCard';
import { UpcomingBillingsStrip } from '../components/UpcomingBillingsStrip';
import { GlobalSearch } from '../components/GlobalSearch';
import { BillingStatusBanner, BillingStatePreviewControl, billingPreviewProps, BillingPreviewState } from '../components/BillingStatusBanner';
// Mock Data
const atRiskMembers = [{
  id: 1,
  name: 'James Wilson',
  issue: 'Skipped 2 shipments',
  inactiveDays: 45,
  riskFactors: 2 // Multiple factors
}, {
  id: 2,
  name: 'Emma Thompson',
  issue: 'Payment failed',
  inactiveDays: 12,
  riskFactors: 1
}, {
  id: 3,
  name: 'Michael Chen',
  issue: 'No engagement',
  inactiveDays: 60,
  riskFactors: 1
}];
const paymentIssues = [{
  id: 1,
  name: 'James Anderson',
  subscriptionId: 'SUB-23138',
  issue: 'Card Expiring',
  days: 8,
  retriesSent: 2,
  retriesTotal: 3
}, {
  id: 2,
  name: 'Ethan White',
  subscriptionId: 'SUB-23130',
  issue: 'Failed Payment',
  days: 3,
  retriesSent: 3,
  retriesTotal: 3
}, {
  id: 3,
  name: 'Victoria Moore',
  subscriptionId: 'SUB-23121',
  issue: 'Insufficient Funds',
  days: 5,
  retriesSent: 1,
  retriesTotal: 3
}, {
  id: 4,
  name: 'Daniel Scott',
  subscriptionId: 'SUB-23120',
  issue: 'Card Declined',
  days: 7,
  retriesSent: 2,
  retriesTotal: 3
}];
const upcomingRenewals = [{
  period: 'Next 7 days',
  count: 23,
  needsAttention: 2,
  filterValue: 'next-7-days'
}, {
  period: 'Next 30 days',
  count: 67,
  needsAttention: 5,
  filterValue: 'next-30-days'
}, {
  period: 'Next 3 months',
  count: 156,
  needsAttention: 8,
  filterValue: 'next-3-months'
}];
const teamNotes = [{
  id: 1,
  author: 'Sarah',
  member: 'James Wilson',
  note: 'Prefers reds only, no Chardonnay',
  time: '2h ago'
}, {
  id: 2,
  author: 'Mike',
  member: 'Emma Thompson',
  note: 'Called about failed payment, updating card tomorrow',
  time: '4h ago'
}, {
  id: 3,
  author: 'System',
  member: 'Robert Fox',
  note: 'Automated dunning email 2 sent',
  time: '5h ago'
}, {
  id: 4,
  author: 'Sarah',
  member: 'Alice Johnson',
  note: 'Upgraded to 12-bottle case for holiday season',
  time: '1d ago'
}];
// Mock task data for My Tasks widget
const myTasks = [{
  id: '1',
  title: "Chase Emma's failed payment",
  priority: 'Urgent' as const,
  dueDate: 'Overdue',
  isOverdue: true,
  linkedTo: {
    id: 'SUB-23138',
    type: 'subscription'
  }
}, {
  id: '2',
  title: "Follow up on David's tier upgrade",
  priority: 'Medium' as const,
  dueDate: 'Due today',
  isOverdue: false,
  linkedTo: {
    id: 'MEM-1087',
    type: 'member'
  }
}, {
  id: '3',
  title: 'Call Sarah about address change',
  priority: 'High' as const,
  dueDate: 'Due tomorrow',
  isOverdue: false,
  linkedTo: {
    id: 'Sarah Johnson',
    type: 'contact'
  }
}, {
  id: '4',
  title: 'Review March wine allocation',
  priority: 'Medium' as const,
  dueDate: 'In 3 days',
  isOverdue: false,
  linkedTo: null
}, {
  id: '5',
  title: 'Prepare April club newsletter',
  priority: 'Low' as const,
  dueDate: 'Next week',
  isOverdue: false,
  linkedTo: null
}];
function getPriorityDotColor(priority: string) {
  switch (priority) {
    case 'Urgent':
      return 'bg-[#EF4444]';
    case 'High':
      return 'bg-[#F97316]';
    case 'Medium':
      return 'bg-[#F59E0B]';
    case 'Low':
      return 'bg-[#10B981]';
    default:
      return 'bg-[#94A3B8]';
  }
}
// Contextual Trend Indicator Component
interface ContextualTrendProps {
  value: number;
  label: string;
  direction: 'up' | 'down';
  isInverse?: boolean; // For metrics where increase is bad (e.g., churn rate)
}
function ContextualTrend({
  value,
  label,
  direction,
  isInverse = false
}: ContextualTrendProps) {
  // Determine if the change is positive for the business
  const isPositiveChange = isInverse ? direction === 'down' : direction === 'up';
  const color = isPositiveChange ? '#10B981' : '#EF4444';
  const Icon = direction === 'up' ? TrendingUp : TrendingDown;
  return <div className="flex items-center gap-1.5" data-id="element-13865" data-mp="13z5m9gipmrm7">
      <Icon className="w-3 h-3" style={{
      color
    }} data-id="element-13866" data-mp="3h7krnuh8ais6" />
      <span className="text-xs font-medium" style={{
      color
    }} data-id="element-13867" data-mp="3h95qvxehmpwd">
        {direction === 'up' ? '+' : '-'}
        {Math.abs(value)}%
      </span>
      <span className="text-xs text-[#94A3B8]" data-id="element-13868" data-mp="38fsm9y729zug">{label}</span>
    </div>;
}
// Retry Progress Dots Component
interface RetryProgressProps {
  sent: number;
  total: number;
}
function RetryProgress({
  sent,
  total
}: RetryProgressProps) {
  return <div className="flex items-center gap-1" title={`${sent}/${total} retries sent`} data-id="element-13869" data-mp="3yge267w04we">
      {Array.from({
      length: total
    }).map((_, index) => <div key={index} className={`w-1.5 h-1.5 rounded-full ${index < sent ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-13870" data-mp="3r4fqyflbsn7i" data-mpi={index} />)}
    </div>;
}
// Helper function to get urgency styling
function getUrgencyStyle(days: number) {
  if (days < 1) {
    return {
      bg: 'bg-[#FEF2F2]',
      border: 'border-l-2 border-[#EF4444]',
      textColor: 'text-[#EF4444]'
    };
  } else if (days <= 3) {
    return {
      bg: 'bg-[#FFFBEB]',
      border: 'border-l-2 border-[#F59E0B]',
      textColor: 'text-[#F59E0B]'
    };
  } else {
    return {
      bg: '',
      border: '',
      textColor: 'text-[#EF4444]'
    };
  }
}
// Helper function to get issue color
function getIssueColor(issue: string) {
  if (issue.includes('Expiring')) return 'text-[#F59E0B]';
  return 'text-[#EF4444]';
}
// Risk Severity Component
interface RiskSeverityProps {
  inactiveDays: number;
  riskFactors: number;
}
function getRiskLevel(inactiveDays: number, riskFactors: number): 'high' | 'medium' | 'low' {
  if (inactiveDays >= 45 || riskFactors >= 2) return 'high';
  if (inactiveDays >= 14 || riskFactors === 1) return 'medium';
  return 'low';
}
function RiskDot({
  inactiveDays,
  riskFactors
}: RiskSeverityProps) {
  const level = getRiskLevel(inactiveDays, riskFactors);
  const colors = {
    high: 'bg-[#EF4444]',
    medium: 'bg-[#F59E0B]',
    low: 'bg-[#FCD34D]'
  };
  return <div className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} data-id="element-13871" data-mp="1fjzvp6w29nw4" />;
}
// Tooltip Component
function InfoTooltip({
  text
}: {
  text: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative inline-block" data-id="element-13872" data-mp="15u9kh6yskng0">
      <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-[#94A3B8] hover:text-[#64748B] transition-colors" data-id="element-13873" data-mp="15w48iu5d0vob">
        <Info className="w-4 h-4" data-id="element-13874" data-mp="1eufxysuhpnf6" />
      </button>
      {showTooltip && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-[#0F172A] text-white text-xs rounded-lg p-3 shadow-lg z-10" data-id="element-13875" data-mp="1esv5r9vnegkh">
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#0F172A]" data-id="element-13876" data-mp="1ojzhm0q11lb3" />
          {text}
        </div>}
    </div>;
}
export function WineClubDashboard() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('This Month');
  const [billingPreview, setBillingPreview] = useState<BillingPreviewState>('none');
  // Get all notes from ActivityContext
  const {
    getAllNotes
  } = useActivities();
  const allNotes = getAllNotes();
  const recentNotes = allNotes.slice(0, 5);
  // Get tasks from TaskContext
  const {
    getTasksByUser,
    addTask,
    updateTask
  } = useTasks();
  const allMyTasks = getTasksByUser('Sarah Chen');
  const openTasks = allMyTasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled');
  const completedTasks = allMyTasks.filter(t => t.status === 'Complete');
  // Track which completed tasks the user has cleared from the widget
  const [clearedTaskIds, setClearedTaskIds] = useState<Set<string>>(new Set());
  // Show open tasks first, then completed (excluding cleared ones), max 8 total
  const visibleCompleted = completedTasks.filter(t => !clearedTaskIds.has(t.id));
  const displayTasks = [...openTasks, ...visibleCompleted].slice(0, 8);
  const handleClearTask = (taskId: string) => {
    setClearedTaskIds(prev => new Set([...prev, taskId]));
  };
  const handleClearAllCompleted = () => {
    const allCompletedIds = completedTasks.map(t => t.id);
    setClearedTaskIds(prev => new Set([...prev, ...allCompletedIds]));
  };
  // Create Task modal state
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  // Responsive state - only check for mobile
  const [isMobile, setIsMobile] = useState(false);
  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768); // Less than tablet (md breakpoint)
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Date Range State
  const [selectedRange, setSelectedRange] = useState<DateRangeOption>('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // Helper for comparison labels
  const getComparisonLabel = (range: DateRangeOption) => {
    switch (range) {
      case 'today':
        return 'vs yesterday';
      case '7days':
        return 'vs prev 7 days';
      case '30days':
        return 'vs prev 30 days';
      case '90days':
        return 'vs prev 90 days';
      case 'year':
        return 'vs last year';
      case 'custom':
        return 'vs prior period';
      default:
        return 'vs last month';
    }
  };
  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };
  // Sort payment issues by urgency (fewest days first)
  const sortedPaymentIssues = [...paymentIssues].sort((a, b) => a.days - b.days);
  // Sort at-risk members by risk severity
  const sortedAtRiskMembers = [...atRiskMembers].sort((a, b) => {
    const levelOrder = {
      high: 0,
      medium: 1,
      low: 2
    };
    const aLevel = getRiskLevel(a.inactiveDays, a.riskFactors);
    const bLevel = getRiskLevel(b.inactiveDays, b.riskFactors);
    if (levelOrder[aLevel] !== levelOrder[bLevel]) {
      return levelOrder[aLevel] - levelOrder[bLevel];
    }
    // If same level, sort by inactive days (most inactive first)
    return b.inactiveDays - a.inactiveDays;
  });
  // Mobile incompatibility screen
  if (isMobile) {
    return <div className="bg-[#F1F5F9] min-h-screen flex items-center justify-center p-6" data-id="element-13877" data-mp="r47unprl2qtg">
        <div className="max-w-md text-center" data-id="element-13878" data-mp="1euttcqfp6yqs">
          <div className="mb-6 flex justify-center" data-id="element-13879" data-mp="1iv44sim9ehd1">
            <div className="w-20 h-20 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-13880" data-mp="1ium336gtvaxq">
              <Smartphone className="w-10 h-10 text-[#0EA5E9]" data-id="element-13881" data-mp="1537hqszg6xni" />
            </div>
          </div>
          <h1 className="text-[24px] font-semibold text-[#0F172A] mb-3" data-id="element-13882" data-mp="1a190tdwviwyz">
            Mobile Version Coming Soon
          </h1>
          <p className="text-[14px] text-[#64748B] mb-6 leading-relaxed" data-id="element-13883" data-mp="1xqju6abw9p33">
            The Wine Club Dashboard is optimized for desktop and tablet devices.
            We're working on a mobile-friendly version that will be available
            soon.
          </p>
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-4" data-id="element-13884" data-mp="27hbmowgrw89d">
            <p className="text-[13px] text-[#475569] font-medium mb-2" data-id="element-13885" data-mp="22g4dqu2w9314">
              For the best experience:
            </p>
            <ul className="text-[13px] text-[#64748B] text-left space-y-1" data-id="element-13886" data-mp="1xhazgzfs97zw">
              <li data-id="element-13887" data-mp="1thamzkc8v6vh">• Use a tablet (768px or wider)</li>
              <li data-id="element-13888" data-mp="1ti2984jybs93">• Use a desktop computer</li>
              <li data-id="element-13889" data-mp="1oohhuvcv5wiv">• Rotate your device to landscape</li>
            </ul>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-13890" data-mp="1jqxrix2w5tak">
      {/* Prototype-only banner state control */}
      <div className="mb-4" data-id="element-13891" data-mp="1eswccn8l6e4d">
        <BillingStatePreviewControl value={billingPreview} onChange={setBillingPreview} data-id="element-13892" data-mp="1epi7n8cg9str" />
      </div>

      {/* Billing status banner — one banner, picked by severity */}
      <div className="mb-6" data-id="element-13893" data-mp="15r6gn57o8t0s">
        <BillingStatusBanner {...billingPreviewProps(billingPreview)} upgradePlanName="Growth" data-id="element-13894" data-mp="15t0wi5435uy0" />
      </div>

      {/* NavBar */}
      <div className="mb-6" data-id="element-13895" data-mp="3letyrr86j3hw">
        <NavBar tabs={[]} title="Wine Club Dashboard" data-id="element-13896" data-mp="3le0nqe26trty" />
      </div>

      {/* Global search */}
      <GlobalSearch data-id="element-13897" data-mp="3qfzpxkm6yipb" />

      {/* Date Range Tabs */}
      <div className="mb-6" data-id="element-13898" data-mp="3vdj6wsa9x9ky">
        <DateRangeTabs value={selectedRange} onChange={setSelectedRange} customStartDate={customStartDate} customEndDate={customEndDate} onCustomDateChange={handleCustomDateChange} data-id="element-13899" data-mp="3vcq6sa5tlibs" />
      </div>

      {/* Top Row: Key Metrics with Contextual Colors */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-13900" data-mp="32mrbvy4y2qz7">
        {/* Active Members - Green when up, Red when down */}
        <StatCard label="Active Members" value={847} trend={<ContextualTrend value={12} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-13902" data-mp="3cgqeszcuees7" />} data-id="element-13901" data-mp="3cfyrebtefnd0" />

        {/* Monthly Revenue - Green when up, Red when down */}
        <StatCard label="Monthly Revenue" value="$42,350" trend={<ContextualTrend value={8} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-13904" data-mp="phmevrh50zt0" />} data-id="element-13903" data-mp="2jwrvs0r30m7x" />

        {/* Churn Rate - INVERSE: Red when up (bad), Green when down (good) */}
        <StatCard label="Churn Rate" value="2.1%" trend={<ContextualTrend value={0.5} label={getComparisonLabel(selectedRange)} direction="up" isInverse={true} data-id="element-13906" data-mp="zaa8kdctmnni" />} data-id="element-13905" data-mp="z8gzkreqjrcc" />

        {/* Avg Order Value - Green when up, Red when down */}
        <StatCard label="Avg Order Value" value="$186" trend={<ContextualTrend value={12} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-13908" data-mp="147k48aqszvjs" />} data-id="element-13907" data-mp="194trbjqpzv4g" />
      </div>

      {/* Upcoming billings */}
      <UpcomingBillingsStrip data-id="element-13909" data-mp="1i6a1bqdmhv85" />

      <hr className="my-10 border-[#CBD5E1]" data-id="element-13910" data-mp="1d88a2i61mpll" />

      {/* Row 2: At Risk & Renewals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-13911" data-mp="3jyg56u8b5obs">
        {/* At Risk Members - Payment Issues */}
        <Card padding="none" className="flex flex-col" data-id="element-13912" data-mp="3ezlfd8bxazi6">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-13913" data-mp="3f166enz140ba">
            <div className="flex items-center gap-2" data-id="element-13914" data-mp="1ujn9vgp5kxnq">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" data-id="element-13915" data-mp="1zfcfa0nut8ry" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-13916" data-mp="1lp8u7d0pnl43">
                Payment Issues
              </h3>
            </div>
            <Badge variant="error" data-id="element-13917" data-mp="1pp9lhy8wrqsk">2 Critical</Badge>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1" data-id="element-13918" data-mp="1bq9y0tx4thxz">
            <div className="space-y-3" data-id="element-13919" data-mp="1bsw5yunxpzgq">
              {sortedPaymentIssues.map(member => {
              const urgencyStyle = getUrgencyStyle(member.days);
              const issueColor = getIssueColor(member.issue);
              return <div key={member.id} className={`flex justify-between items-start p-3 rounded-md transition-colors ${urgencyStyle.bg} ${urgencyStyle.border}`} data-id="element-13920" data-mp="16rgiyx275ivp">
                    <div className="flex-1" data-id="element-13921" data-mp="2x4xhy6lsgo5y">
                      <button onClick={() => navigate(`/subscriptions/${member.subscriptionId}`)} className="font-medium text-sm text-[#0EA5E9] hover:text-[#0284C7] transition-colors text-left" data-id="element-13922" data-mp="3169dstv9a5ml">
                        {member.name}
                      </button>
                      <div className={`text-[13px] ${issueColor} flex items-center gap-1 mt-1`} data-id="element-13923" data-mp="90bj9cg5dz8n">
                        <CreditCard className="w-3 h-3" data-id="element-13924" data-mp="4ypzr893ymph" /> {member.issue}
                      </div>
                    </div>
                    <div className="text-right ml-4" data-id="element-13925" data-mp="wnjnrs5r549m">
                      <div className={`text-[13px] font-semibold ${urgencyStyle.textColor}`} data-id="element-13926" data-mp="wn9sbp1xu4wj">
                        {member.days} {member.days === 1 ? 'day' : 'days'} left
                      </div>
                      <div className="mt-1.5 flex justify-end" data-id="element-13927" data-mp="ntn36hs8jzwb">
                        <RetryProgress sent={member.retriesSent} total={member.retriesTotal} data-id="element-13928" data-mp="ix6e9ld7phi6" />
                      </div>
                    </div>
                  </div>;
            })}
            </div>
            <button onClick={() => navigate('/subscriptions', {
            state: {
              filterValues: {
                status: ['at-risk']
              }
            }
          })} className="w-full mt-4 text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] flex items-center justify-center gap-1 transition-colors" data-id="element-13929" data-mp="1bkskz2hql299">
              View all payment issues <ArrowRight className="w-4 h-4" data-id="element-13930" data-mp="1wvztqeu5jiha" />
            </button>
          </div>
        </Card>

        {/* At Risk Members - Engagement */}
        <Card padding="none" className="flex flex-col" data-id="element-13931" data-mp="21u1ed4787puv">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-13932" data-mp="26u5v8vc1qmlm">
            <div className="flex items-center gap-2" data-id="element-13933" data-mp="2bl49lpdh5am8">
              <Users className="w-5 h-5 text-[#F59E0B]" data-id="element-13934" data-mp="2blnu9duied3y" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-13935" data-mp="2gk8mor3irshu">
                Engagement Risk
              </h3>
              <InfoTooltip text="Members flagged due to: extended inactivity, skipped shipments, failed payments, or no recent engagement." data-id="element-13936" data-mp="2kmvg2wfu9lh5" />
            </div>
            <Badge variant="warning" data-id="element-13937" data-mp="3avc202dr5wwy">3 Members</Badge>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1" data-id="element-13938" data-mp="3axoqkye0n5iz">
            <div className="space-y-3" data-id="element-13939" data-mp="35vhca25prddh">
              {sortedAtRiskMembers.map(member => <div key={member.id} className="flex justify-between items-start gap-3" data-id="element-13940" data-mp="3kuba9julvqjf">
                  <div className="flex items-start gap-2 flex-1" data-id="element-13941" data-mp="3fuyeuapo5b58">
                    <div className="mt-1.5" data-id="element-13942" data-mp="3fsly20vnd6tr">
                      <RiskDot inactiveDays={member.inactiveDays} riskFactors={member.riskFactors} data-id="element-13943" data-mp="2s4b3tgkgi5eg" />
                    </div>
                    <div className="flex-1" data-id="element-13944" data-mp="2n7c8iup23seo">
                      <button onClick={() => navigate(`/subscriptions/${member.id}`)} className="font-medium text-sm text-[#0EA5E9] hover:text-[#0284C7] transition-colors text-left" data-id="element-13945" data-mp="31uxeqxh8a7qt">
                        {member.name}
                      </button>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-13946" data-mp="29dbj4rjzvhqz">
                        {member.issue}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-[#EF4444] bg-[#FEF2F2] px-2 py-1 rounded whitespace-nowrap" data-id="element-13947" data-mp="jwg050n5ta1d">
                    {member.inactiveDays}d inactive
                  </span>
                </div>)}
            </div>
            <div className="mt-4 flex justify-end" data-id="element-13948" data-mp="9x8d1lqmi112">
              <button onClick={() => navigate('/subscriptions', {
              state: {
                filterValues: {
                  status: ['at-risk']
                }
              }
            })} className="text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] flex items-center gap-1 transition-colors" data-id="element-13949" data-mp="exmoldrfnt3u">
                View all at-risk members <ArrowRight className="w-4 h-4" data-id="element-13950" data-mp="tpdbmmtqhyqh" />
              </button>
            </div>
          </div>
        </Card>

        {/* Upcoming Renewals */}
        <Card padding="none" className="flex flex-col" data-id="element-13951" data-mp="3ecgjb1bgl71h">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-13952" data-mp="3efvwtzphi80s">
            <div className="flex items-center gap-2" data-id="element-13953" data-mp="2koo087wph8t8">
              <Calendar className="w-5 h-5 text-[#0EA5E9]" data-id="element-13954" data-mp="2kp63vplp6pb6" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-13955" data-mp="2oq9jcazu2jbv">
                Upcoming Renewals
              </h3>
            </div>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1 flex flex-col justify-between" data-id="element-13956" data-mp="2asb6ja15uuyh">
            <div className="space-y-2" data-id="element-13957" data-mp="2fredj00pbb93">
              {upcomingRenewals.map((item, idx) => <div key={idx} onClick={() => navigate('/subscriptions', {
              state: {
                filterValues: {
                  nextOrderDate: [item.filterValue]
                }
              }
            })} className="flex justify-between items-start p-3 bg-[#F8FAFC] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer group" data-id="element-13958" data-mp="20zm04tkgw7a0" data-mpi={idx}>
                  <span className="text-sm text-[#475569] font-medium" data-id="element-13959" data-mp="1i11bd861tdzj">
                    {item.period}
                  </span>
                  <div className="text-right" data-id="element-13960" data-mp="1mzb83n7f8mn9">
                    <div className="text-xl font-bold text-[#0F172A] group-hover:text-[#0EA5E9] transition-colors" data-id="element-13961" data-mp="13eujez3wt1gd">
                      {item.count}
                    </div>
                    {item.needsAttention > 0 && <div className="flex items-center gap-1 mt-1 justify-end" data-id="element-13962" data-mp="1dcqr1gxn1e9q">
                        <AlertTriangle className="w-3 h-3 text-[#F59E0B]" data-id="element-13963" data-mp="1dans27eicasv" />
                        <span className="text-xs text-[#F59E0B] font-medium" data-id="element-13964" data-mp="18cm71gxiu1er">
                          {item.needsAttention} need attention
                        </span>
                      </div>}
                  </div>
                </div>)}
            </div>
            <div className="mt-4 flex justify-end" data-id="element-13965" data-mp="1w3rm3tvn0po0"></div>
          </div>
        </Card>
      </div>

      {/* Row 3: Activity & Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-13966" data-mp="1r4yhkqybw8zz">
        {/* Subscription Activity */}
        <div className="lg:col-span-2" data-id="element-13967" data-mp="261xmu85pjpra">
          <SubscriptionActivityCard data-id="element-13968" data-mp="212uqdc3smrux" />
        </div>

        {/* Right Column: My Tasks, Recent Team Activity & Recovery */}
        <div className="space-y-6" data-id="element-13969" data-mp="286ubmqqsjdg5">
          {/* My Tasks Widget */}
          <Card padding="none" className="flex flex-col" data-id="element-13970" data-mp="2d4mcia9ey9wh">
            <div className="p-5 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-13971" data-mp="2d55j4utm5bv7">
              <div className="flex items-center gap-2.5" data-id="element-13972" data-mp="2i25r5ba0wooy">
                <div className="w-6 h-6 rounded bg-[#0EA5E9] flex items-center justify-center" data-id="element-13973" data-mp="2mvh1vnhyhpm5">
                  <CheckCircle className="w-4 h-4 text-white" data-id="element-13974" data-mp="2rt0defn9roae" />
                </div>
                <h3 className="text-[15px] font-bold text-[#0F172A]" data-id="element-13975" data-mp="2vuly2n61p6v5">
                  My Tasks
                </h3>
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B] text-xs font-medium border border-[#E2E8F0]" data-id="element-13976" data-mp="30vrryobfz069">
                  {openTasks.length} open
                </span>
              </div>
              <div className="flex bg-[#F1F5F9] rounded-lg p-0.5" data-id="element-13977" data-mp="1bfa7r4gi4ryn">
                {['Mine', 'Team', 'All'].map((tab, idx) => <button key={tab} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${idx === 0 ? 'bg-[#0F172A] text-white shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-13978" data-mp="13wj4r0odgnv8" data-mpi={idx}>
                    {tab}
                  </button>)}
              </div>
            </div>
            <div className="bg-white rounded-b-lg" data-id="element-13979" data-mp="p7d7hi93h7pz">
              <div className="divide-y divide-[#F1F5F9]" data-id="element-13980" data-mp="p8y1n6rhyonb">
                {displayTasks.map(task => {
                const isOverdue = new Date(task.dueDate) < new Date('2026-02-12');
                const isDueToday = task.dueDate === 'Feb 12 2026';
                const isCompleted = task.status === 'Complete';
                return <div key={task.id} className="px-5 py-3.5 flex items-start gap-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer group" data-id="element-13981" data-mp="1cyinpyiy620t">
                      <div className="mt-0.5 flex-shrink-0" data-id="element-13982" data-mp="1iljz46g9djj">
                        <button onClick={e => {
                      e.stopPropagation();
                      if (isCompleted) {
                        updateTask(task.id, {
                          status: 'Open',
                          completedAt: undefined,
                          completedBy: undefined
                        });
                      } else {
                        updateTask(task.id, {
                          status: 'Complete',
                          completedAt: new Date().toISOString(),
                          completedBy: 'Sarah Chen'
                        });
                      }
                    }} className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${isCompleted ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-13983" data-mp="3sntrekmr5xr9">
                          {isCompleted && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-13984" data-mp="1z271g6t6o3ij">
                              <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-13985" data-mp="iogsaln7ip21" />
                            </svg>}
                        </button>
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => navigate('/operations/tasks')} data-id="element-13986" data-mp="31ygqi7ob15u1">
                        <div className={`text-sm font-medium truncate ${isCompleted ? 'text-[#94A3B8] line-through' : 'text-[#0F172A]'}`} data-id="element-13987" data-mp="3axbynpawspzz">
                          {task.title}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-id="element-13988" data-mp="2j5wfy6ch3nf1">
                          <span className={`text-xs ${isCompleted ? 'text-[#94A3B8]' : 'text-[#64748B]'}`} data-id="element-13989" data-mp="2j3rz6nth6txc">
                            {task.assignee?.name || 'Sarah Admin'}
                          </span>
                          <span className="text-[#CBD5E1]" data-id="element-13990" data-mp="2n7rh9auem0st">·</span>
                          {isCompleted ? <span className="text-xs text-[#10B981] font-medium flex items-center gap-1" data-id="element-13991" data-mp="2n5n4e3ej1rge">
                              ✓ Completed yesterday
                            </span> : <span className={`text-xs font-medium flex items-center gap-1 ${isOverdue ? 'text-[#EF4444]' : isDueToday ? 'text-[#F59E0B]' : 'text-[#64748B]'}`} data-id="element-13992" data-mp="1vfsd4173zrp3">
                              <Clock className="w-3 h-3" data-id="element-13993" data-mp="3e8spq9uqvhc" />{' '}
                              {isOverdue ? `Overdue (${Math.abs(Math.ceil((new Date(task.dueDate).getTime() - new Date('2026-02-12').getTime()) / (1000 * 60 * 60 * 24)))} days)` : isDueToday ? 'Due today' : `Due ${new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}`}
                            </span>}
                          {task.linkedTo && !isCompleted && <>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[11px] font-medium text-[#0EA5E9] border border-[#E2E8F0]" data-id="element-13994" data-mp="3ljc5zy9z2dsu">
                                {task.linkedTo.label || task.linkedTo.id}
                              </span>
                            </>}
                        </div>
                      </div>
                      {isCompleted && <button onClick={e => {
                    e.stopPropagation();
                    handleClearTask(task.id);
                  }} className="mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[#94A3B8] hover:text-[#EF4444] p-0.5 rounded hover:bg-[#FEF2F2]" title="Clear task" data-id="element-13995" data-mp="2qb0g50ikisav">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-13996" data-mp="2bkt350bby1hk">
                            <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-13997" data-mp="26lg8hte7vq4f" />
                          </svg>
                        </button>}
                    </div>;
              })}
              </div>
              <div className="px-5 py-3 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-13998" data-mp="1sotadnaj2twx">
                <button onClick={() => navigate('/operations/tasks')} className="text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] transition-colors flex items-center gap-1" data-id="element-13999" data-mp="3rzknqr28i4gb">
                  View all tasks <ArrowRight className="w-3.5 h-3.5" data-id="element-14000" data-mp="2vxrlx6znjivm" />
                </button>
                {visibleCompleted.length > 0 && <button onClick={handleClearAllCompleted} className="text-[12px] text-[#94A3B8] font-medium hover:text-[#64748B] transition-colors" data-id="element-14001" data-mp="30v07zinx8725">
                    Clear completed
                  </button>}
              </div>
            </div>
          </Card>

          {/* Team Notes Feed */}
          <Card padding="none" data-id="element-14002" data-mp="2d4dzxrdx2ms5">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-14003" data-mp="2i51ycy4h65we">
              <div className="flex items-center gap-2" data-id="element-14004" data-mp="23817sxy0sgzi">
                <MessageSquare className="w-5 h-5 text-[#64748B]" data-id="element-14005" data-mp="239lyudl4lhsm" />
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-14006" data-mp="287x92pfc1ubd">
                  Recent Team Activity
                </h3>
              </div>
              <Button variant="ghost" size="sm" className="text-[#0EA5E9]" onClick={() => navigate('/operations/activity-log', {
              state: {
                filterByNotes: true
              }
            })} data-id="element-14007" data-mp="1elgdyu3yy3h">
                View All
              </Button>
            </div>
            <div className="p-0 bg-white rounded-b-lg" data-id="element-14008" data-mp="1lwdsdzrekpih">
              {recentNotes.map((note, idx) => {
              const customerName = customerNames[note.customerId] || 'Unknown Customer';
              const authorInitial = note.author?.charAt(0) || 'S';
              // Calculate relative time
              const noteDate = new Date(note.date);
              const now = new Date();
              const diffMs = now.getTime() - noteDate.getTime();
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              let timeAgo;
              if (diffHours < 1) {
                timeAgo = 'Just now';
              } else if (diffHours < 24) {
                timeAgo = `${diffHours}h ago`;
              } else if (diffDays === 1) {
                timeAgo = '1d ago';
              } else {
                timeAgo = `${diffDays}d ago`;
              }
              return <div key={note.id} className={`p-4 flex gap-3 hover:bg-[#F8FAFC] transition-colors ${idx !== recentNotes.length - 1 ? 'border-b border-[#F1F5F9]' : ''}`} data-id="element-14009" data-mp="3lc5paskxmwni" data-mpi={idx}>
                    <div className="w-8 h-8 rounded-full bg-[#E0F2FE] text-[#0369A1] flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-14010" data-mp="3blt3yy5amzgb">
                      {authorInitial}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14011" data-mp="31m28ou8wsgf6">
                      <div className="flex justify-between items-start gap-2" data-id="element-14012" data-mp="31pgaa09r0llk">
                        <p className="text-sm text-[#0F172A]" data-id="element-14013" data-mp="2xls33ip13s0u">
                          <span className="font-semibold" data-id="element-14014" data-mp="2sngufbagznk7">
                            {note.author || 'Admin'}
                          </span>{' '}
                          added note on{' '}
                          <button onClick={() => navigate(`/contacts/${note.customerId}`)} className="font-semibold text-[#0EA5E9] hover:underline" data-id="element-14015" data-mp="qusnorymahzo">
                            {customerName}
                          </button>
                        </p>
                        <span className="text-xs text-[#94A3B8] whitespace-nowrap" data-id="element-14016" data-mp="13bbhtnjki468">
                          {timeAgo}
                        </span>
                      </div>
                      <p className="text-sm text-[#475569] mt-1 bg-[#F8FAFC] p-2 rounded border border-[#E2E8F0] italic line-clamp-2" title={note.description} data-id="element-14017" data-mp="1d2ob8jl0l2ed">
                        "{note.description}"
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </Card>

          {/* Recovery Summary */}
          <Card padding="none" data-id="element-14018" data-mp="tfqmu9cx245z">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-14019" data-mp="th1pg5uml5vi">
              <div className="flex items-center gap-2" data-id="element-14020" data-mp="20v4s4qt0moc3">
                <RefreshCw className="w-5 h-5 text-[#10B981]" data-id="element-14021" data-mp="24tm2dj7qgexq" />
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-14022" data-mp="5pvs3p61w8pj">
                  Recovery
                </h3>
              </div>
              <Badge variant="success" data-id="element-14023" data-mp="pr8vrdrczvm">84% Rate</Badge>
            </div>
            <div className="p-6 bg-white rounded-b-lg" data-id="element-14024" data-mp="eqbtmt38h5rr">
              <div className="flex justify-between items-end mb-2" data-id="element-14025" data-mp="epshjt09lbln">
                <span className="text-sm text-[#64748B]" data-id="element-14026" data-mp="9sk0jvqurdlz">
                  Recovered Revenue
                </span>
                <span className="text-xl font-bold text-[#10B981]" data-id="element-14027" data-mp="3i7tv8isexgjq">$4,250</span>
              </div>
              <div className="w-full bg-[#F1F5F9] rounded-full h-2 mb-4" data-id="element-14028" data-mp="3e7a2rsisb13x">
                <div className="bg-[#10B981] h-2 rounded-full" style={{
                width: '84%'
              }} data-id="element-14029" data-mp="3ry5d798xlq9m"></div>
              </div>
              <div className="text-xs text-[#64748B]" data-id="element-14030" data-mp="3n4amhluwsb11">
                18 of 21 failed payments recovered this month
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Actions Strip */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-[#E2E8F0] shadow-lg rounded-full px-6 py-3 flex items-center gap-4 z-50" data-id="element-14031" data-mp="19zxeu8a3vlco">
        <span className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mr-2" data-id="element-14032" data-mp="1esi898p3nwfo">
          Quick Actions
        </span>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-14033" data-mp="15xu09as540n6">
          <AlertTriangle className="w-4 h-4" data-id="element-14034" data-mp="1tozi1vhn5304" />
          View At-Risk
        </button>
        <div className="w-px h-4 bg-[#E2E8F0]" data-id="element-14035" data-mp="1xpjbop309ohi"></div>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-14036" data-mp="1xmx973uz1z65">
          <CreditCard className="w-4 h-4" data-id="element-14037" data-mp="1oo1zhhspy6y3" />
          Process Pending
        </button>
        <div className="w-px h-4 bg-[#E2E8F0]" data-id="element-14038" data-mp="8albn6wuebgy"></div>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-14039" data-mp="88qrvvxb4peg">
          <Mail className="w-4 h-4" data-id="element-14040" data-mp="sj1m6o9zep46" />
          Klaviyo Campaign
        </button>
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={taskData => {
      addTask({
        ...taskData,
        status: 'Open',
        createdBy: 'Sarah Chen',
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        }
      });
      setIsCreateTaskModalOpen(false);
    }} data-id="element-14041" data-mp="nrtcdr4qp1qh" />
    </div>;
}